import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
// xlsx-populate's package.json "browser" field isn't set up in a way that Angular's
// webpack build picks up automatically, so its Node entry point (lib/XlsxPopulate.js)
// gets bundled instead, which pulls in fs/crypto/stream and fails to compile. Import
// its prebuilt browser bundle directly instead - Node built-ins are already stubbed
// out there. We don't use password protection, so the smaller "no-encryption" build
// is used (saves ~1.3MB over the encryption-capable one).
// eslint-disable-next-line @typescript-eslint/no-var-requires
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate-no-encryption.min.js';

export interface ExcelSheet {
	sheetName: string;
	/**
	 * Sheet content as an array of rows (array-of-arrays). Each inner array is a
	 * row of cell values. This is the shape the exception reports produce.
	 */
	data: any[][];
	/**
	 * Optional styling instructions. When provided, the sheet is rendered with
	 * xlsx-populate so borders / fonts / number formats can be applied. When
	 * omitted the sheet is written as plain values.
	 */
	style?: ExcelSheetStyle;
}

export interface ExcelSheetStyle {
	/**
	 * Zero-based row indices (relative to `data`) that make up the report header
	 * info block (Export Time, Customer Name, Site Name, Time Period, Selected
	 * Filter Values ...). Rendered bold, 14pt.
	 */
	headerInfoRows?: number[];
	/**
	 * Zero-based row indices that are report section titles
	 * (e.g. "Low Battery Utilization Report"). Rendered 14pt.
	 */
	titleRows?: number[];
	/**
	 * Report tables to box with thin borders. Each entry covers the column-header
	 * row plus its data rows. Column-header row is rendered bold.
	 */
	tables?: ExcelTableStyle[];
}

export interface ExcelTableStyle {
	/** Zero-based index of the bold column-header row. */
	headerRow: number;
	/** Zero-based index of the first data row (inclusive). */
	dataStartRow: number;
	/** Zero-based index of the last data row (inclusive). */
	dataEndRow: number;
	/** Zero-based first column of the table. */
	startCol: number;
	/** Zero-based last column of the table. */
	endCol: number;
	/**
	 * Zero-based column indices (relative to the sheet) whose data cells hold a
	 * ratio (e.g. 0.29) that should display as a percentage (29%).
	 */
	percentColumns?: number[];
}

@Injectable({
	providedIn: 'root'
})
export class ExcelService {

	private static readonly HEADER_FONT_SIZE = 14;
	private static readonly TITLE_FONT_SIZE = 14;
	private static readonly PERCENT_FORMAT = '0%';
	private static readonly FONT_FAMILY = 'Arial';

	constructor() { }

	async exportExcel(sheets: ExcelSheet[], fileName: string): Promise<void> {
		const workbook = await XlsxPopulate.fromBlankAsync();

		sheets.forEach((sheet, index) => {
			const sheetName = this.checkSheetName(sheet.sheetName, index);
			const wsSheet = index === 0
				? workbook.sheet(0).name(sheetName)
				: workbook.addSheet(sheetName);

			this.writeSheet(wsSheet, sheet);
		});

		const arrayBuffer: ArrayBuffer = await workbook.outputAsync();
		const blob = new Blob([arrayBuffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		saveAs(blob, fileName);
	}

	private writeSheet(wsSheet: any, sheet: ExcelSheet): void {
		const data = sheet.data || [];

		// 1. Write raw values (rows / cols are 1-based in xlsx-populate).
		data.forEach((row, r) => {
			(row || []).forEach((value, c) => {
				if (value !== undefined && value !== null && value !== '')
					wsSheet.row(r + 1).cell(c + 1).value(value);
			});
		});

		// Default font for the whole used range.
		this.applyBaseFont(wsSheet, data);

		if (sheet.style)
			this.applyStyle(wsSheet, sheet.style);

		this.autoFitColumns(wsSheet, data);
	}

	private applyBaseFont(wsSheet: any, data: any[][]): void {
		const rowCount = data.length;
		if (!rowCount) return;
		const colCount = data.reduce((max, row) => Math.max(max, (row || []).length), 0);
		if (!colCount) return;

		wsSheet.range(1, 1, rowCount, colCount).style({
			fontFamily: ExcelService.FONT_FAMILY,
			horizontalAlignment: 'left',
		});
	}

	private applyStyle(wsSheet: any, style: ExcelSheetStyle): void {
		// Header info block: bold + 14pt.
		(style.headerInfoRows || []).forEach(rowIndex => {
			const excelRow = wsSheet.row(rowIndex + 1);
			excelRow.style({ bold: true, fontSize: ExcelService.HEADER_FONT_SIZE });
		});

		// Section titles: 14pt.
		(style.titleRows || []).forEach(rowIndex => {
			wsSheet.row(rowIndex + 1).style('fontSize', ExcelService.TITLE_FONT_SIZE);
		});

		// Tables: bold header row, thin borders around the whole table,
		// percentage formatting on ratio columns.
		(style.tables || []).forEach(table => this.applyTableStyle(wsSheet, table));
	}

	private applyTableStyle(wsSheet: any, table: ExcelTableStyle): void {
		const topRow = table.headerRow + 1;
		const bottomRow = table.dataEndRow + 1;
		const leftCol = table.startCol + 1;
		const rightCol = table.endCol + 1;

		// Bold column-header row.
		wsSheet.range(table.headerRow + 1, leftCol, table.headerRow + 1, rightCol)
			.style('bold', true);

		// Thin borders on all sides of every cell in the table.
		if (bottomRow >= topRow) {
			wsSheet.range(topRow, leftCol, bottomRow, rightCol).style('border', {
				top: { style: 'thin' },
				bottom: { style: 'thin' },
				left: { style: 'thin' },
				right: { style: 'thin' },
			});
		}

		// Percentage formatting on score/ratio columns (data rows only).
		if (table.percentColumns && table.dataEndRow >= table.dataStartRow) {
			for (const col of table.percentColumns) {
				wsSheet.range(table.dataStartRow + 1, col + 1, table.dataEndRow + 1, col + 1)
					.style('numberFormat', ExcelService.PERCENT_FORMAT);
			}
		}
	}

	private autoFitColumns(wsSheet: any, data: any[][]): void {
		const colCount = data.reduce((max, row) => Math.max(max, (row || []).length), 0);

		for (let c = 0; c < colCount; c++) {
			let maxChars = 10; // minimum width
			for (const row of data) {
				const value = row?.[c];
				if (value !== undefined && value !== null) {
					const text = String(value);
					// extra padding for non-ASCII (e.g. CJK) glyphs
					const estimated = text.length + (/[\u3000-\u9fff\uac00-\ud7af]/.test(text) ? text.length : 0);
					if (estimated > maxChars) maxChars = estimated;
				}
			}
			wsSheet.column(c + 1).width(Math.min(maxChars + 2, 60));
		}
	}

	// Sanitize sheet name: max 31 chars, cannot contain : \ / ? * [ ]
	private checkSheetName(name: string, index: number): string {
		if (!name) return `Sheet${index + 1}`;

		const invalid = /[:\\\/\?\*\[\]]/g;
		const clean = name.replace(invalid, '_').trim();
		if (!clean) return `Sheet${index + 1}`;
		return clean.length > 31 ? clean.substring(0, 31) : clean;
	}
}